import { serverTimestamp } from 'firebase/firestore';

function asString(v) {
  if (v == null) return '';
  return String(v).trim();
}

function bool(v) {
  return v === true;
}

/**
 * Normaliza contactos desde campos legacy y bloque nuevo `contactos`.
 * No muta el objeto original.
 *
 * Prioridad (según requerimiento):
 * - correo <- email || correo || contactos.correo.valor
 * Ajuste por UX real del admin:
 * - `telefono` en el proyecto se usa como “Teléfono Móvil (WhatsApp)”, por lo que NO debe poblar teléfono fijo.
 * - telefonoFijo <- telefonoFijo || telefono_fijo || contactos.telefonoFijo.valor
 * - whatsapp <- whatsapp || wsap || telefono || contactos.whatsapp.valor
 * - telefonoMovil <- celular || movil || telefonoMovil || contactos.telefonoMovil.valor
 */
export function normalizeEmpresaContactos(empresa) {
  const e = empresa && typeof empresa === 'object' ? empresa : {};
  const c = e.contactos && typeof e.contactos === 'object' ? e.contactos : {};

  const correoValor = asString(c?.correo?.valor) || asString(e.email) || asString(e.correo);
  const fijoValor = asString(c?.telefonoFijo?.valor) || asString(e.telefonoFijo) || asString(e.telefono_fijo);
  const whatsappValor = asString(c?.whatsapp?.valor) || asString(e.whatsapp) || asString(e.wsap) || asString(e.telefono);
  const movilValor =
    asString(c?.telefonoMovil?.valor) || asString(e.celular) || asString(e.movil) || asString(e.telefonoMovil);

  const mk = (key, valor) => {
    const block = c?.[key] && typeof c[key] === 'object' ? c[key] : {};
    return {
      valor,
      verificado: bool(block.verificado),
      fechaVerificacion: block.fechaVerificacion ?? null,
      verificadoPor: block.verificadoPor ?? null,
      metodo: block.metodo ?? null,
    };
  };

  return {
    correo: mk('correo', correoValor),
    telefonoFijo: mk('telefonoFijo', fijoValor),
    whatsapp: mk('whatsapp', whatsappValor),
    telefonoMovil: mk('telefonoMovil', movilValor),
  };
}

/**
 * Deriva estado global según regla:
 * empresaVerificada = tieneTelefonoVerificado (correo no activa badge público)
 */
export function computeVerificacionContactos(contactos) {
  const c = contactos && typeof contactos === 'object' ? contactos : {};
  const tieneTelefonoVerificado =
    bool(c?.telefonoFijo?.verificado) || bool(c?.whatsapp?.verificado) || bool(c?.telefonoMovil?.verificado);
  const tieneCorreoVerificado = bool(c?.correo?.verificado);
  const empresaVerificada = tieneTelefonoVerificado;
  return { tieneTelefonoVerificado, tieneCorreoVerificado, empresaVerificada };
}

/**
 * Estado público de verificación.
 * - Preferir nuevo derivado si existe.
 * - Compatibilidad: acepta flags legacy (contacto_verificado) y (verificado) como fallback.
 */
export function buildPublicVerificationState(empresa) {
  const e = empresa && typeof empresa === 'object' ? empresa : {};
  if (e?.verificacionContactos?.empresaVerificada === true) return true;
  if (e?.verificacionContactos?.tieneTelefonoVerificado === true) return true;
  if (e?.contacto_verificado === true) return true;
  return e?.verificado === true;
}

/**
 * Construye payload seguro para persistir normalizado + derivados.
 * Agrega `fechaActualizacion` por servidor.
 */
export function buildContactosUpdatePayload({ empresa, contactos, verificadoPor, observacionGeneral } = {}) {
  const normalized = contactos || normalizeEmpresaContactos(empresa);
  const derived = computeVerificacionContactos(normalized);
  return {
    contactos: normalized,
    verificacionContactos: {
      ...derived,
      fechaActualizacion: serverTimestamp(),
      ...(verificadoPor ? { verificadoPor } : {}),
      ...(observacionGeneral ? { observacionGeneral: String(observacionGeneral).trim() } : {}),
    },
  };
}

